import React, { useEffect, useState } from "react";
import { Calendar, Clock, MapPin, Heart, Leaf, Users, ArrowRight, CheckCircle, Star, Phone, Mail, Globe, Flower2, BookOpen, Coffee, Home, Brain, Sparkles, Compass, Baby, Shield, MessageCircle, Target, Bed, Apple } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import NavBar from '@/components/NavBar';
import { NewsletterSignup } from '@/components/NewsletterSignup';
import { BOOKING_URL } from '@/constants/urls';
import familyImageWebP from '@/assets/family.webp';
import familyImage from '@/assets/family.jpg';
import { ReturnToTop } from '@/components/ReturnToTop';
import ScrollDownArrow from '@/components/ScrollDownArrow';

const PregnancyPage = () => {
  const [isLoading, setIsLoading] = useState(false);

  // Force scroll to top on page load
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Add dynamic meta tags and structured data
  useEffect(() => {
    // Update document title and meta tags
    document.title = "Chinese Medicine for Pregnancy Sunshine Coast | Prenatal Care & Support";
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', "Nurturing you and your baby - body, mind, and spirit. Gentle Chinese medicine care for pregnancy support, morning sickness, and birth preparation in Currimundi.");
    }

    // Update Open Graph tags
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', "Chinese Medicine for Pregnancy Sunshine Coast | Prenatal Care & Support");
    }

    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute('content', "Nurturing you and your baby - body, mind, and spirit. Gentle Chinese medicine care for pregnancy support, morning sickness, and birth preparation in Currimundi.");
    }

    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) {
      ogUrl.setAttribute('content', "https://womenandtheway.com.au/pregnancy");
    }

    const ogImage = document.querySelector('meta[property="og:image"]');
    if (ogImage) {
      ogImage.setAttribute('content', "https://womenandtheway.com.au/family.jpg");
    }

    // Update Twitter Card
    const twitterTitle = document.querySelector('meta[name="twitter:title"]');
    if (twitterTitle) {
      twitterTitle.setAttribute('content', "Chinese Medicine for Pregnancy Sunshine Coast | Prenatal Care & Support");
    }

    // Add LocalBusiness Schema for pregnancy services
    const pregnancySchema = {
      "@context": "https://schema.org",
      "@type": "HealthAndBeautyBusiness",
      "name": "Women and The Way - Chinese Medicine for Pregnancy",
      "image": "https://womenandtheway.com.au/family.jpg",
      "description": "Nurturing you and your baby - body, mind, and spirit. Gentle Chinese medicine care for pregnancy support, morning sickness, and birth preparation in Currimundi.",
      "url": "https://womenandtheway.com.au/pregnancy",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "700 Nicklin Way",
        "addressLocality": "Currimundi",
        "addressRegion": "QLD",
        "addressCountry": "AU"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": -26.5270,
        "longitude": 153.0890
      },
      "priceRange": "$$",
      "serviceType": "Pregnancy Care, Prenatal Acupuncture, Chinese Medicine, Birth Preparation",
      "sameAs": [
        "https://www.facebook.com/Womenandtheway",
        "https://www.instagram.com/women_and_the_way"
      ]
    };

    // Remove existing schema if any
    const existingSchema = document.querySelector('script[type="application/ld+json"]');
    if (existingSchema) {
      existingSchema.remove();
    }

    // Add new schema
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(pregnancySchema);
    document.head.appendChild(script);

    // Cleanup function
    return () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, []);

  const handleBooking = async () => {
    setIsLoading(true);
    try {
      window.open(BOOKING_URL, '_blank');
    } catch (error) {
      console.error('Booking error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="min-h-screen bg-gradient-to-b from-white via-white to-amber-50">
        <NavBar />
        
        {/* Hero Section */}
        <section className="relative py-20 px-4 md:px-6">
          <div className="container mx-auto max-w-6xl">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold mb-6 bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">
                  Chinese Medicine for Pregnancy
                </h1>
                <p className="text-xl md:text-2xl text-sage-600 mb-6 font-medium">
                  Nurturing you and your baby - body, mind, and spirit.
                </p>
                <p className="text-lg text-sage-700 mb-8 leading-relaxed">
                  Pregnancy is a beautiful journey of transformation, but it can also bring unique challenges to your body and mind. At Women and the Way, we provide gentle, safe care throughout your pregnancy to support your health and your baby's development.
                </p>
                <p className="text-lg text-sage-700 mb-8 leading-relaxed">
                  Whether you're experiencing morning sickness, back pain, anxiety, or simply want to optimize your pregnancy health, our treatments help you feel your best during this special time. Our approach combines the wisdom of Traditional Chinese Medicine with modern understanding of pregnancy physiology. Many of our clients also benefit from our <a href="/birth-prep" className="text-sage-600 hover:text-sage-800 underline transition-colors">birth preparation</a> and <a href="/postpartum" className="text-sage-600 hover:text-sage-800 underline transition-colors">postpartum care services</a>.
                </p>
                <Button 
                  size="lg" 
                  className="bg-sage-600 hover:bg-sage-700 text-white font-medium text-base px-8 py-4 rounded-lg transition-all duration-200 flex items-center justify-center gap-2"
                  onClick={handleBooking}
                  disabled={isLoading}
                >
                  {isLoading ? 'Booking...' : 'Book Now'}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
              <div className="relative">
                <picture>
                  <source srcSet={familyImageWebP} type="image/webp" />
                  <img 
                    src={familyImage} 
                    alt="Pregnant woman receiving gentle acupuncture for pregnancy support" 
                    className="rounded-2xl shadow-lg w-full h-auto"
                  />
                </picture>
              </div>
            </div>
          </div>
        </section>

        {/* Why Pregnancy Support Matters */}
        <section className="py-16 px-4 md:px-6 bg-sage-50">
          <div className="container mx-auto max-w-4xl">
            <div className="text-center mb-12">
              <h2 className="font-serif text-3xl md:text-4xl font-bold mb-6 flex items-center justify-center gap-3 bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">
                <Baby className="w-8 h-8 text-sage-600" />
                Why Pregnancy Support Matters
              </h2>
            </div>
            <div className="bg-white rounded-2xl shadow-lg border-2 border-amber-200/50 p-8 md:p-12">
              <p className="text-lg text-sage-700 mb-6 leading-relaxed">
                In Traditional Chinese Medicine, pregnancy is a precious window to nourish the life within - and the mother who carries it. By supporting the flow of Qi and Blood, calming the nervous system, and balancing Yin and Yang, Chinese medicine helps maintain harmony during the many changes of pregnancy.
              </p>
              <p className="text-lg text-sage-700 leading-relaxed">
                Our treatments work with your body - not against it - to help ease discomforts, support your baby's development, and prepare you for birth with strength and grace.
              </p>
            </div>
          </div>
        </section>

        {/* What We Offer */}
        <section className="py-16 px-4 md:px-6">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-12">
              <h2 className="font-serif text-3xl md:text-4xl font-bold mb-6 bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">
                What We Offer
              </h2>
              <p className="text-xl text-sage-600 mb-8">
                Pregnancy Care In-Clinic, At-Home, or Online
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8 mb-12">
              <Card className="bg-white border-2 border-amber-200/50 shadow-lg rounded-2xl hover:shadow-xl transition-shadow">
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-4">
                    <Home className="w-6 h-6 text-sage-600" />
                    <h3 className="font-serif text-2xl font-semibold text-sage-800">In-Clinic Sessions</h3>
                  </div>
                  <p className="text-sage-700 mb-4">
                    Visit our calming Currimundi clinic for a peaceful pause and personalised support at any stage of your pregnancy.
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <span className="text-lg">🪡</span>
                      <p className="text-sage-700">Gentle acupuncture to ease nausea, reduce swelling, support sleep, and calm the mind</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="text-lg">🌿</span>
                      <p className="text-sage-700">Chinese herbal medicine for safe, trimester-specific support for digestion, immunity, or emotional balance</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="text-lg">🌀</span>
                      <p className="text-sage-700">Birth preparation sessions in the third trimester to encourage optimal positioning and readiness</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="text-lg">🤍</span>
                      <p className="text-sage-700">Emotional support for stress, overwhelm, or anxiety</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white border-2 border-amber-200/50 shadow-lg rounded-2xl hover:shadow-xl transition-shadow">
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-4">
                    <Heart className="w-6 h-6 text-sage-600" />
                    <h3 className="font-serif text-2xl font-semibold text-sage-800">At-Home Support</h3>
                  </div>
                  <p className="text-sage-700 mb-4 font-medium">
                    Can't make it to clinic or simply prefer the comfort of your own space?
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <span className="text-lg">🪡</span>
                      <p className="text-sage-700">Acupuncture, moxibustion, and safe bodywork in your home</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="text-lg">🔥</span>
                      <p className="text-sage-700">Mother warming and acupressure guidance</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="text-lg">🥣</span>
                      <p className="text-sage-700">Nourishment planning, herbal broths, and tea suggestions</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="text-lg">🌸</span>
                      <p className="text-sage-700">Calm, connected care without needing to travel</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white border-2 border-amber-200/50 shadow-lg rounded-2xl hover:shadow-xl transition-shadow">
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-4">
                    <Globe className="w-6 h-6 text-sage-600" />
                    <h3 className="font-serif text-2xl font-semibold text-sage-800">Online Support</h3>
                  </div>
                  <p className="text-sage-700 mb-4 font-medium">
                    Support wherever you are.
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <span className="text-lg">💬</span>
                      <p className="text-sage-700">Phone or video consults to check in, adjust care plans, or prepare for birth</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="text-lg">🌿</span>
                      <p className="text-sage-700">Custom herbal prescriptions posted to your door or picked up locally</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="text-lg">📚</span>
                      <p className="text-sage-700">Birth prep guidance, acupressure for labour, and self-care rituals</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Pregnancy Care Timeline */}
        <section className="py-16 px-4 md:px-6 bg-sage-50">
          <div className="container mx-auto max-w-4xl">
            <div className="text-center mb-12">
              <h2 className="font-serif text-3xl md:text-4xl font-bold mb-6 bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">
                Pregnancy Care Timeline
              </h2>
            </div>
            
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="item-1" className="bg-white rounded-xl border-2 border-amber-200/50 shadow-lg mb-4">
                <AccordionTrigger className="px-6 py-4 text-left">
                  <div className="flex items-center gap-3">
                    <Flower2 className="w-6 h-6 text-sage-600" />
                    <span className="font-serif text-xl font-semibold text-sage-800">First Trimester</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-6">
                  <div className="space-y-4">
                    <p className="text-sage-700 font-medium">✨ Support for morning sickness, fatigue, anxiety, and implantation stability</p>
                    <p className="text-sage-700 italic">👉 Calm the Shen, harmonise the Stomach, build Kidney Qi</p>
                  </div>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-2" className="bg-white rounded-xl border-2 border-amber-200/50 shadow-lg mb-4">
                <AccordionTrigger className="px-6 py-4 text-left">
                  <div className="flex items-center gap-3">
                    <Heart className="w-6 h-6 text-sage-600" />
                    <span className="font-serif text-xl font-semibold text-sage-800">Second Trimester</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-6">
                  <div className="space-y-4">
                    <p className="text-sage-700 font-medium">✨ Support for energy, sleep, digestion, emotional balance</p>
                    <p className="text-sage-700 italic">👉 Tonify Blood, support Liver/Spleen harmony</p>
                  </div>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-3" className="bg-white rounded-xl border-2 border-amber-200/50 shadow-lg mb-4">
                <AccordionTrigger className="px-6 py-4 text-left">
                  <div className="flex items-center gap-3">
                    <Baby className="w-6 h-6 text-sage-600" />
                    <span className="font-serif text-xl font-semibold text-sage-800">Third Trimester</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-6">
                  <div className="space-y-4">
                    <p className="text-sage-700 font-medium">✨ Support for back pain, fluid retention, birth prep, and optimal fetal positioning</p>
                    <p className="text-sage-700 italic">👉 Move Qi gently, encourage downward flow, nourish Jing</p>
                  </div>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-4" className="bg-white rounded-xl border-2 border-amber-200/50 shadow-lg">
                <AccordionTrigger className="px-6 py-4 text-left">
                  <div className="flex items-center gap-3">
                    <Sparkles className="w-6 h-6 text-sage-600" />
                    <span className="font-serif text-xl font-semibold text-sage-800">Post-Dates</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-6">
                  <div className="space-y-4">
                    <p className="text-sage-700 font-medium">✨ Support for natural labour encouragement and emotional readiness</p>
                    <p className="text-sage-700 italic">👉 Activate movement gently with moxa, acupuncture, and pressure points</p>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </section>

        {/* Common Conditions We Support */}
        <section className="py-16 px-4 md:px-6">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-12">
              <h2 className="font-serif text-3xl md:text-4xl font-bold mb-6 bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">
                Common Conditions We Support
              </h2>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { western: "Morning sickness", chinese: "Stomach Qi rebellion, Spleen deficiency" },
                { western: "Fatigue & insomnia", chinese: "Qi and Blood deficiency, Yin deficiency" },
                { western: "Anxiety or mood shifts", chinese: "Heart/Spleen disharmony, Liver constraint" },
                { western: "Back pain & sciatica", chinese: "Kidney deficiency, Qi/Blood stagnation" },
                { western: "Constipation, bloating, reflux", chinese: "Spleen Qi deficiency, Stomach heat" },
                { western: "Fluid retention", chinese: "Spleen Qi deficiency, Dampness" },
                { western: "Breech positioning", chinese: "Cold in Uterus, Qi constraint" },
                { western: "Pre-birth preparation", chinese: "Qi stagnation, Blood deficiency, emotional tension" }
              ].map((condition, index) => (
                <Card key={index} className="bg-white border-2 border-amber-200/50 shadow-sm rounded-xl hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <h3 className="font-semibold text-sage-800 mb-2">{condition.western}</h3>
                    <p className="text-sage-600 text-sm italic">{condition.chinese}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* What Makes Our Care Unique */}
        <section className="py-16 px-4 md:px-6 bg-sage-50">
          <div className="container mx-auto max-w-4xl">
            <div className="text-center mb-12">
              <h2 className="font-serif text-3xl md:text-4xl font-bold mb-6 bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">
                What Makes Our Care Unique
              </h2>
            </div>
            
            <div className="grid md:grid-cols-2 gap-6">
              {[
                { icon: "🫶", title: "Gentle and individualised", desc: "We tailor each treatment to your trimester, symptoms, and energy" },
                { icon: "🌿", title: "Natural, safe, and evidence-informed", desc: "Rooted in ancient wisdom and adapted to modern needs" },
                { icon: "📚", title: "Birth education and empowerment", desc: "We'll share tools and acupressure points to support you at home and during labour" },
                { icon: "👶", title: "Baby-inclusive care", desc: "Because your wellbeing directly supports theirs" }
              ].map((feature, index) => (
                <Card key={index} className="bg-white border-2 border-amber-200/50 shadow-sm rounded-xl hover:shadow-lg transition-shadow">
                  <CardContent className="p-6 text-center">
                    <div className="text-3xl mb-3">{feature.icon}</div>
                    <h3 className="font-serif text-xl font-semibold text-sage-800 mb-2">{feature.title}</h3>
                    <p className="text-sage-700">{feature.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Support Tools */}
        <section className="py-16 px-4 md:px-6">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-12">
              <h2 className="font-serif text-3xl md:text-4xl font-bold mb-6 bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">
                Pregnancy Support Tools in Clinic
              </h2>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { icon: Coffee, title: "Trimester-specific herbal teas", desc: "And tinctures for safe pregnancy support" },
                { icon: Heart, title: "Moxa sticks", desc: "And home-use instructions for warming therapy" },
                { icon: Leaf, title: "Castor oil packs", desc: "For late-pregnancy comfort and support" },
                { icon: BookOpen, title: "Acupressure guides", desc: "For labour and breech support techniques" },
                { icon: BookOpen, title: "Recommended reads", desc: "Including The Golden Month, Gentle Birth, and The First Forty Days" }
              ].map((tool, index) => (
                <Card key={index} className="bg-white border-2 border-amber-200/50 shadow-sm rounded-xl hover:shadow-lg transition-shadow">
                  <CardContent className="p-6 text-center">
                    <tool.icon className="w-8 h-8 text-sage-600 mx-auto mb-3" />
                    <h3 className="font-serif text-lg font-semibold text-sage-800 mb-2">{tool.title}</h3>
                    <p className="text-sage-700 text-sm">{tool.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 px-4 md:px-6 bg-sage-50">
          <div className="container mx-auto max-w-4xl text-center">
            <h2 className="font-serif text-3xl md:text-4xl font-bold mb-6 bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">
              Book Your Pregnancy Session
            </h2>
            <p className="text-xl text-sage-700 mb-8">
              Whether you're newly pregnant or nearing birth, Chinese medicine offers safe, nourishing support to carry you through with ease. Let's help you feel held, strong, and ready.
            </p>
            
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <div className="flex items-center justify-center gap-3 text-sage-700">
                <MapPin className="w-5 h-5 text-sage-600" />
                <a 
                  href="https://maps.google.com/?q=700+Nicklin+Way+Currimundi+QLD" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="hover:text-sage-800 transition-colors"
                >
                  700 Nicklin Way, Currimundi
                </a>
              </div>
              <div className="flex items-center justify-center gap-3 text-sage-700">
                <Phone className="w-5 h-5 text-sage-600" />
                <a 
                  href="tel:0415537784" 
                  className="hover:text-sage-800 transition-colors"
                >
                  0415 537 784
                </a>
              </div>
              <div className="flex items-center justify-center gap-3 text-sage-700">
                <Mail className="w-5 h-5 text-sage-600" />
                <a 
                  href="mailto:womenandtheway@gmail.com" 
                  className="hover:text-sage-800 transition-colors"
                >
                  womenandtheway@gmail.com
                </a>
              </div>
            </div>
            
            <Button 
              size="lg" 
              className="w-full sm:w-auto text-center break-words !whitespace-normal bg-sage-600 hover:bg-sage-700 text-white font-medium text-base px-8 py-4 rounded-lg transition-all duration-200 !flex !flex-col sm:!flex-row items-center justify-center gap-2 mx-auto min-w-0 min-h-[60px] sm:min-h-[auto]"
              onClick={handleBooking}
              disabled={isLoading}
            >
              <span className="block break-words whitespace-normal text-center">{isLoading ? 'Booking...' : '🌱 Start where you are. We\'ll walk with you from there.'}</span>
              <ArrowRight className="w-5 h-5 ml-2 flex-shrink-0" />
            </Button>
          </div>
        </section>

        <ScrollDownArrow />
        <ReturnToTop />
      </div>
    </>
  );
};

export default PregnancyPage; 